import {computed, effect, Injectable, NgZone, signal, WritableSignal} from "@angular/core";
import {Perfil, Rol, TokenInterface} from "../interfaces/login.interface";
import {HttpClient} from "@angular/common/http";
import {environment} from "../../../../environments/environment.development";
import Swal from "sweetalert2";
import {Router} from "@angular/router";

const apiVucUrl = environment.apiVucUrl

@Injectable({
    providedIn: 'root'
})
export class LoginServices {
    private monitorHandle: any;
    public tipoDocumento: WritableSignal<string> = signal('')
    /**
     * Session actual del usuario autenticado. Aca se almacena toda la info del perfil de usuario.
     */
    private _session: WritableSignal<TokenInterface | null> = signal(null);
    private _role: Rol | null = null;
    private sessionLoaded = signal(false);
    private _sessionToken = computed(() => this._session()?.token)

    constructor(private http: HttpClient, private router: Router, private zone: NgZone) {
        this.sessionExpirationMonitor()
    }

    login(user: string, password: string) {
        return this.http.post<TokenInterface>(apiVucUrl + `api/v1.0/auth/token`, {
            'usuario': user,
            'clave': password
        }, {observe: 'response'})
    }

    logout() {
        return this.http.post(apiVucUrl + `api/v1.0/auth/logout`, {
            'uniqueId': this.session()?.perfil.uniqueId
        }, {observe: 'response'})
    }

    removerSession(caller?: string) {
        console.log(`#DEBUG removerSession call from ->${caller}`)
        sessionStorage.removeItem("userSession")
        this._session.set(null)
        this.clearMonitor()
    }

    hasRole(specificRole: string): boolean {
        const currentSession = this.session();
        if (!currentSession || !currentSession.perfil?.roles) {
            return false;
        }
        return currentSession.perfil?.roles.some(role => role.rol === specificRole);
    }

    hasRoles(roles: string[]): boolean {
        const currentSession = this.session();
        if (!currentSession || !currentSession.perfil?.roles) {
            return false;
        }
        // Using Set for O(1) average time complexity lookups
        const rolesToCheck = new Set(roles);
        //console.log("ROLES DEL USUARIO", currentSession.perfil?.roles);
        return currentSession.perfil?.roles.some(sessionRol => rolesToCheck.has(sessionRol.rol));
    }

    getDefaultRoute(perfil: Perfil): string | undefined {
        let defaultRoute: string | undefined = '/notfound';
        const defaultRolRoutes = ['atencion.cliente'];
        const rolPrioritario = defaultRolRoutes.map(rolRoute => perfil.roles.find(rol => rol.rol === rolRoute)).find(rol => rol !== undefined);
        defaultRoute = rolPrioritario?.routerLink ?? perfil.roles[0]?.routerLink ?? '/notfound';
        return defaultRoute;
    }

    getDefaultRole(): Rol | null {
        const currentSession = this.session();
        if (!currentSession || !currentSession.perfil?.roles) {
            return null;
        }
        if (currentSession.perfil && currentSession.perfil?.roles && currentSession.perfil?.roles.length > 0) {
            this._role = currentSession.perfil?.roles[0];
        }
        return this._role;
    }

    get isValid() {
        return typeof this.expireToken === 'number' && Math.floor(Date.now() / 1000) < this.expireToken;
    }

    get expireToken() {
        if (!this.token) return null;
        return JSON.parse(atob(this.token?.split('.')[1]))?.exp
    }

    get token() {
        return this._sessionToken() ?? JSON.parse(sessionStorage.getItem('userSession') ?? '{}')?.token
    }

    get session(): WritableSignal<TokenInterface | null> {
        return this._session;
    }

    set session(value: WritableSignal<TokenInterface | null>) {
        this._session = value;
    }

    /**
     * Sync only once on startup
     */
    loadSessionEffect = effect(() => {
        console.log(`#DEBUG loadSessionEffect call`)
        const userSession = sessionStorage.getItem('userSession');
        if (userSession) {
            console.log(`#DEBUG session loaded`)
            this.session.set(JSON.parse(userSession));
            this.sessionLoaded.set(true); // 🔥 signal load complete
        }
        console.log(`#DEBUG session status: ${userSession != null}`);
    }, {allowSignalWrites: true})
    /**
     * Sync current session when changes.
     */
    syncSessionEffect = effect(() => {
        const session = this.session();
        if (session) {
            sessionStorage.setItem("userSession", JSON.stringify(session));
            console.log(`#DEBUG session updated`);
        }
    }, {allowSignalWrites: true});
    isRolCliente = computed(() => {
        return this.hasRoles(['cliente_ventanilla'])
    });
    isRolGerenteTecnico = computed(() => {
        return this.hasRoles(['ministro', 'gerente', 'jefe', 'atencion.cliente'])
    });
    isRolTecnicoResolutor = computed(() => {
        return this.hasRoles(['ministro', 'gerente', 'jefe', 'tecnico_resolutor'])
    });

    isRolTecnico = computed(() => this.hasRole('tecnico_resolutor'));

    isRolAtencionCliente = computed(() => this.hasRole('atencion.cliente'));
    
    isRolJefeRegistro = computed(() => {
        return this.hasRoles(['jefe.registro'])
    });
    isRolTecnicoRegistro = computed(() => {
        return this.hasRoles(['tecnico.registro'])
    });
    isRolGerente = computed(() => this.hasRole('gerente'));

    /**
     * Ejecuta la validación y redirecciona hacia login cuando expira el tiempo de la sesión.
     */
    public sessionExpirationMonitor() {
        console.log(`#DEBUG sessionExpirationMonitor call`);
        let alertShown = false;
        const tick = () => {
            const session = this._session();
            const token = session?.token;
            if (!token) {
                this.clearMonitor();
                return;
            }
            let exp = 0;
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                exp = typeof payload.exp === 'number' ? payload.exp : 0;
            } catch {
                exp = 0;
            }
            const now = Math.floor(Date.now() / 1000);
            const remaining = exp - now;
            console.log(`#DEBUG session remaining: ${remaining}s`);
            if (remaining <= 30 && remaining > 0 && !alertShown) {
                alertShown = true;
                Swal.fire({
                    title: 'Sesión expirará pronto',
                    text: 'Su sesión expirará en menos de 30 segundos.',
                    icon: 'warning',
                    timer: 10000,
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    timerProgressBar: true,
                    confirmButtonText: 'OK',
                });
            }
            if (remaining <= 0) {
                this.zone.run(() => {
                    console.log(`#DEBUG Session expired, remaining<=0`);
                    this.removerSession('sessionExpirationMonitor');
                    sessionStorage.clear();
                    localStorage.clear();
                    this.router.navigateByUrl('/auth/login', {skipLocationChange: false}).then(() => {
                        console.log('Token expired, redirecting to login.');
                    }).catch(err => {
                        console.error('Navigation error:', err);
                    });
                });
            }
        };
        tick();
        this.clearMonitor();
        this.monitorHandle = setInterval(tick, 1000);
    }

    private clearMonitor() {
        console.log(`#DEBUG clearMonitor call`);
        if (this.monitorHandle) {
            clearInterval(this.monitorHandle);
            this.monitorHandle = null;
            console.log(`#DEBUG monitorHandle cleared`);
        }
    }

}
